import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { Outlet, useLocation } from 'react-router-dom';

import { LandingPageLayout, LandingStaticPageLayout } from '@app/layouts/LandingPageLayout';

import { staticPageTitle } from '@pages/StaticPageExample/model/content';

import { ROUTES } from '@shared/config/routes';

import { LandingHeader, type LandingHeaderProps } from './LandingHeader';
import { landingNavItems, type LandingSectionId } from '../model/content';

export interface LandingOutletContext {
  onVisibleSectionChange: (sectionId: LandingSectionId | null) => void;
}

export const LandingRouteFrame = () => {
  const { pathname } = useLocation();
  const [activeSection, setActiveSection] = useState<LandingSectionId | null>(null);
  const pendingSectionRef = useRef<LandingSectionId | null>(null);
  const pendingSectionTimeoutRef = useRef<number | null>(null);
  const isLandingPage = pathname === ROUTES.HOME;
  const isStaticPage = pathname === ROUTES.TERMS_PRIVACY || pathname === ROUTES.LEGAL;

  const clearPendingSection = useCallback(() => {
    pendingSectionRef.current = null;

    if (pendingSectionTimeoutRef.current !== null) {
      window.clearTimeout(pendingSectionTimeoutRef.current);
      pendingSectionTimeoutRef.current = null;
    }
  }, []);

  const handleSectionClick = (sectionId: LandingSectionId) => {
    setActiveSection(sectionId);

    if (sectionId !== activeSection) {
      pendingSectionRef.current = sectionId;

      if (pendingSectionTimeoutRef.current !== null) {
        window.clearTimeout(pendingSectionTimeoutRef.current);
      }

      pendingSectionTimeoutRef.current = window.setTimeout(() => {
        clearPendingSection();
      }, 1500);
    } else {
      clearPendingSection();
    }

    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const handleVisibleSectionChange = (sectionId: LandingSectionId | null) => {
    if (pendingSectionRef.current) {
      if (sectionId === pendingSectionRef.current) {
        setActiveSection(sectionId);
        clearPendingSection();
      }

      return;
    }

    setActiveSection(sectionId);
  };

  useEffect(() => {
    return () => {
      clearPendingSection();
    };
  }, [clearPendingSection]);

  useLayoutEffect(() => {
    clearPendingSection();
    setActiveSection(null);
  }, [clearPendingSection, pathname]);

  const headerProps: LandingHeaderProps<LandingSectionId> = isLandingPage
    ? {
        activeSection,
        items: landingNavItems,
        onSectionClick: handleSectionClick,
      }
    : {
        title: isStaticPage ? staticPageTitle : 'Page not found',
      };

  const outlet = <Outlet context={{ onVisibleSectionChange: handleVisibleSectionChange }} />;

  return (
    <div className="landing-layout-vars min-h-dvh bg-background-2 text-foreground">
      <LandingHeader {...headerProps} />
      {isLandingPage ? (
        <LandingPageLayout>{outlet}</LandingPageLayout>
      ) : (
        <LandingStaticPageLayout>{outlet}</LandingStaticPageLayout>
      )}
    </div>
  );
};
